import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { FormComponent, ToastService } from '../../../../shared/components';
import {
  CountriesApiClientProvider,
  CreateCountryModel,
  ErrorOrError,
  handleApiErrors,
  ICountriesApiClient,
  ICountriesResponse,
  ICreateCountryRequest,
  Logger,
  UpdateCountryModel
} from '../../../../../common';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription } from 'rxjs';
import { FormInputConfig } from '../../../../shared/interfaces/form-input-config';

@Component({
  selector: 'app-create-update-country',
  imports: [ReactiveFormsModule, DialogModule, CommonModule, FormComponent, TranslateModule],
  templateUrl: './create-update-country.component.html',
  styleUrl: './create-update-country.component.scss'
})
export class CreateUpdateCountryComponent {
  private client: ICountriesApiClient = inject(CountriesApiClientProvider).getClient();
  private toastService: ToastService = inject(ToastService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private ref: DynamicDialogRef = inject(DynamicDialogRef);
  private translate: TranslateService = inject(TranslateService);
  private subscriptions: Subscription[] = [];
  modalData: DynamicDialogConfig = inject(DynamicDialogConfig);

  model: UpdateCountryModel = CreateCountryModel.createDefault();
  formConfigs: FormInputConfig[] = [];
  itemsFormValidationEmit: boolean = false;
  validationErrors = {};
  isLoadingAddEditItem: boolean = false;
  itemId: string = '';

  ngOnInit(): void {
    this.itemId = this.modalData?.data?.item?.id;

    this.initializeItemData();
    this.initForm();
  }

  private initializeItemData(): void {
    const item: ICountriesResponse = this.modalData?.data?.item;

    if (item) {
      this.model = UpdateCountryModel.createFromItem(item);
    }
  }

  private initForm() {
    this.formConfigs = this.model.getFormConfig(this.modalData?.data?.visible, this.translate);
  }

  formValuesChanged(data: any) {
    Logger.debug('formValuesChanged:   ', data);
  }

  async submitForm(request: ICreateCountryRequest): Promise<void> {
    Logger.debug('Submit form inside create-update-country.component', request, this.model);
    this.isLoadingAddEditItem = true;
    this.cdr.detectChanges();

    let req: Observable<void | ICountriesResponse> = this.model.id
      ? this.client.update(this.itemId, request)
      : this.client.create(request);

    req.subscribe({
      next: (response: ICountriesResponse | void) => this.handleActionSuccess(response),
      error: (errors: ErrorOrError[]) => this.handleActionError(errors),
      complete: () => this.finalizeAction()
    });
  }
  private handleActionSuccess(response: ICountriesResponse | void): void {
    Logger.debug('Action Success: ', response);
    if (response) {
      this.toastService.showSuccess(
        this.translate.instant('GENERAL.SUCCESS'),
        this.translate.instant('GENERAL.SUCCESSFULLY_CREATED')
      );
      this.cdr.detectChanges();
      this.onClose(response);
    } else {
      this.toastService.showSuccess(
        this.translate.instant('GENERAL.SUCCESS'),
        this.translate.instant('GENERAL.SUCCESSFULLY_UPDATED')
      );
      this.cdr.detectChanges();
      this.onClose(true);
    }
  }
  private handleActionError(errors: ErrorOrError[]): void {
    Logger.debug('Action Error:', errors);
    if (this.toastService) {
      handleApiErrors(errors, this.toastService);
    }
    this.isLoadingAddEditItem = false;
  }
  private finalizeAction(): void {
    this.isLoadingAddEditItem = false;
    this.itemsFormValidationEmit = false;
  }

  emitValidation() {
    this.itemsFormValidationEmit = !this.itemsFormValidationEmit;
    this.cdr.detectChanges();
  }
  onClose(data?: boolean | ICountriesResponse) {
    this.ref.close(data);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }
}
